/**
 * @documental
 * @export Subtitle
 * @import
 * import { Subtitle } from "capitalrx-components-2.0/lib/typography";
 * @use
 * The `Subtitle` component should be used as the secondary title for body content, alongside additional body content titles
 * [Title](../Title/README.md), [Heading](../Heading/README.md) and [Subheading](../Subheading/README.md).
 * @example
 * import { FC } from "react";
 * import { Subtitle, Paragraph } from "capitalrx-components-2.0/lib/typography";
 *
 * export const Component: FC = () => {
 *   return (
 *     <div>
 *       <Subtitle>Custom Subtitle</Subtitle>
 *       <Paragraph>
 *         The quick brown fox jumps over the lazy dog.
 *       </Paragraph>
 *     </div>
 *   );
 * };
 * @output
 * <div>
 *   <h3 class="subtitle"><span class="semibold">Custom Subtitle</span></h3>
 *   <p class="paragraph">
 *     The quick brown fox jumps over the lazy dog.
 *   </p>
 * </div>
 */
import { FC, PropsWithChildren } from "react";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Semibold } from "../Semibold";
import { TextAlignment } from "../TextAlignment";

import styles from "./styles.module.scss";

/**
 * The Subtitle component properties.
 */
export interface SubtitleProps extends BaseProps {
  /**
   * The text alignment. If not specified defaults to `"left"`.
   */
  readonly textAlign?: TextAlignment;
}

/**
 * Displays subtitle text. The subtitle is automatically spaced apart from its siblings. However, if the subtitle is the
 * first element it will not have any spacing above, and if it is the last element it will not have any spacing below.
 * Additionally, the subtitle is unaffected by text sizing applied to the [Body](../Body/README.md) component.
 */
export const Subtitle: FC<PropsWithChildren<SubtitleProps>> = (props) => {
  const { children, textAlign = "left" } = props;
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, { className: [styles.Subtitle, "subtitle", `align-${textAlign}`] });

  return (
    <h3 {...baseHtmlProps}>
      <Semibold>{children}</Semibold>
    </h3>
  );
};
